import { getHighestRoofPoint } from "building-generation/elements/roofs";
import type { Building } from "building-types/building/buildingTypes";
import type { RoofFace } from "building-types/roofs/roofFace";
import { RoofFaceType } from "building-types/roofs/roofFace";
import type { RoofElevationPlanSettings, RoofPlanSettings } from "building-types/settings/roofPlanSettings";
import type { ViewEntityTable } from "building-types/views/viewEntityTable";
import type { DimensionPrecision } from "building-types/views/viewTypes";
import { tableMerge, type RWTable, type Table } from "common/data/table";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { degreeToRadian } from "common/utils/math";
import { formatWithPrecision } from "doc/dimensions";
import type { ElevationAnnotationCommonParams } from "doc/plans/elevationAnnotationCommonParams";
import type { PlanGeometryBuilderWithCollider } from "doc/plans/planGeometrySource";
import { intervalsOverlap } from "geom/aabb2";
import type { Face } from "geom/brep";
import { faceNew, faceProjectOuterLoopToPlane } from "geom/brep";
import { DIST_EPSILON, DIST_EPSILON_SQUARED } from "geom/core";
import { extrudeFace, extrusionToBRep } from "geom/extrude";
import { intersectFaceRay } from "geom/intersect";
import { line2Vec } from "geom/line2";
import { offsetPolyLine2 } from "geom/offset";
import {
  planeCopy,
  planeDistanceTo,
  planeEval,
  planeIsCoplanar,
  planeMoveAlongNormal,
  planeNormal,
  planeNormalizedDownwardSlopeVector,
  planeProject,
  planeProjectPolyLine3,
} from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polygonUnion } from "geom/polygon";
import {
  polyLine2AABB2,
  polyLine2Area,
  polyLine2ByTwoPoints,
  polyLine2Copy,
  polyLine2End,
  polyLine2Length,
  polyLine2MergeCoincidentVertices,
  polyLine2Start,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3EndPoint, polyLine3EvalEdge, polyLine3OnPlane, polyLine3StartPoint } from "geom/polyLine3";
import { ray2DotProject, ray2PerpDistance } from "geom/ray2";
import type { Vec2 } from "geom/vec2";
import { vec2AngleBetween, vec2Dot, vec2Len } from "geom/vec2";
import { vec3Dot } from "geom/vec3";
import type { ElevMarker } from "lib/house/annotation/tags/elevMarkersTag";
import { downwardSlopeVectorToElevSlopeFormat } from "lib/house/annotation/util";
import type { FaceGroupBSP } from "lib/house/faceGroupBSP";
import type { LazyPropMesh } from "lib/house/plans/perspectiveTypes";
import type { WASMState } from "./assets/loadWasm";
import { POLYGON_OFFSET_FOR_UNION } from "./perspectiveConstants";
import { evalRoofAnnotationSetting, type RoofAnnotationSettings } from "./planSettingsDefault";
import type { SceneBSP, WASMSceneBSP } from "./SceneBSP";

export interface ElevationDrawing extends ElevationAnnotationCommonParams {
  building: Building;
  entityTable: ViewEntityTable | undefined;
  wasmBuildingBSP?: WASMSceneBSP;
  /** @deprecated - to be removed when we're confident in the WASM version */
  jsBuildingBSP?: SceneBSP;
  /**
   * Rust HLR input ("hlr-wasm"): the building's view face groups, kept un-converted so the geometry
   * is wired Rust-side (`hlrPrepareFromFaceGroups`) instead of round-tripping through JS.
   */
  hlrFaceGroups?: FaceGroupBSP[];
  /** Raw (unsimplified) prop meshes deferred for view-space classification in the HLR stage. */
  hlrLazyPropMeshes?: LazyPropMesh[];
  wasm: WASMState;
}

interface RoofSlopeTagInfo {
  roofPlane: Plane;
  distToViewPlane: number;
  slope: number[];
  pos: number[];
  flip: boolean;
}

interface RoofFaceInfo {
  type: RoofFaceType;
  outline: PolyLine2;
  roofPlane: Plane;
  area: number;
  annotationSettings: RoofPlanSettings & RoofElevationPlanSettings;
  isDormer: boolean;
}

function roofFaceInfoNew(
  type: RoofFaceType,
  outline: PolyLine2,
  roofPlane: Plane,
  area: number,
  annotationSettings: RoofPlanSettings & RoofElevationPlanSettings,
  isDormer: boolean,
): RoofFaceInfo {
  return {
    type,
    outline,
    roofPlane,
    area,
    annotationSettings,
    isDormer,
  };
}

function mergeRoofFaceAnnotationSettings(
  a: RoofPlanSettings & RoofElevationPlanSettings,
  b: RoofPlanSettings & RoofElevationPlanSettings,
): RoofPlanSettings & RoofElevationPlanSettings {
  const mergedSettings = { ...b };
  tableMerge(mergedSettings, { ...a }, (aVal, bVal) => bVal || aVal);
  return mergedSettings;
}

function unionAdjacentCoplanarFaces(roofFaces: RoofFace[]): RoofFaceInfo[] {
  const projectedFaceInfos: RoofFaceInfo[][] = [];

  for (const { face, type, annotationSettings, isDormer } of roofFaces) {
    const outline = polyLine2Copy(face.loops[0]);
    const area = polyLine2Area(outline);

    if (area < DIST_EPSILON_SQUARED) {
      continue;
    }

    const enlargedOutlineForUnion = offsetPolyLine2(outline, -POLYGON_OFFSET_FOR_UNION);

    let indexToAddTo = -1;
    for (const planeIndex of Object.keys(projectedFaceInfos)) {
      const { roofPlane, type: groupType } = projectedFaceInfos[planeIndex][0];
      if (!planeIsCoplanar(face.plane, roofPlane) || type !== groupType) {
        continue;
      }
      indexToAddTo = parseInt(planeIndex);
    }

    if (indexToAddTo !== -1) {
      const list = projectedFaceInfos[indexToAddTo];
      const firstPlane = list[0].roofPlane;

      // copy coplanar outline to first face's plane
      const outline3 = polyLine3OnPlane(enlargedOutlineForUnion, face.plane); // 2 to 3
      const outlineOnFirstPlane = planeProjectPolyLine3(firstPlane, outline3); // 3 to 2
      if (polyLine2Area(outlineOnFirstPlane) < DIST_EPSILON_SQUARED) {
        continue;
      }
      list.push(roofFaceInfoNew(type, outlineOnFirstPlane, firstPlane, area, annotationSettings, isDormer));
      continue;
    }

    projectedFaceInfos.push([
      roofFaceInfoNew(type, enlargedOutlineForUnion, face.plane, area, annotationSettings, isDormer),
    ]);
  }

  // union adjacent coplanar faces
  const unionedCoplanarFaces: RoofFaceInfo[] = [];
  for (const coplanarFaceInfos of projectedFaceInfos) {
    const { roofPlane, outline, area, type, annotationSettings, isDormer } = coplanarFaceInfos[0];

    if (coplanarFaceInfos.length === 1) {
      const adjustOutlineBackToOriginal = offsetPolyLine2(outline, POLYGON_OFFSET_FOR_UNION);
      unionedCoplanarFaces.push(
        roofFaceInfoNew(type, adjustOutlineBackToOriginal, roofPlane, area, annotationSettings, isDormer),
      );
      continue;
    }

    const outlines: PolyLine2[][] = [];
    let mergedAnnotationSettings = annotationSettings;
    for (const coplanarFaceInfo of coplanarFaceInfos) {
      outlines.push([coplanarFaceInfo.outline]);

      mergedAnnotationSettings = mergeRoofFaceAnnotationSettings(
        mergedAnnotationSettings,
        coplanarFaceInfo.annotationSettings,
      );
    }

    let unions: PolyLine2[][];
    try {
      unions = polygonUnion(outlines);
    } catch (e) {
      console.error(e);
      console.error(outlines);
      continue;
    }

    for (const union of unions) {
      const projectedOutline = polyLine2MergeCoincidentVertices(union[0]);
      const area = polyLine2Area(projectedOutline);

      if (area < DIST_EPSILON_SQUARED) {
        continue;
      }

      unionedCoplanarFaces.push(
        roofFaceInfoNew(type, projectedOutline, roofPlane, area, mergedAnnotationSettings, isDormer),
      );
    }
  }
  return unionedCoplanarFaces;
}

export function makeRoofHeightElevAnnos(
  topRoofFaceInfos: Table<RoofFace[]>,
  elevMarkers: ElevMarker[],
  plane: Plane,
  out: PlanGeometryBuilderWithCollider,
  dimensionPrecision?: DimensionPrecision,
) {
  const highestRoofPoint = getHighestRoofPoint(topRoofFaceInfos, plane);
  if (highestRoofPoint !== Number.NEGATIVE_INFINITY) {
    const elevMarker: ElevMarker = {
      levelName: `T.O. ROOF`,
      elevationText: `${formatWithPrecision(highestRoofPoint, dimensionPrecision, false, true)}`,
      zLevel: out.yToPaperSpace(highestRoofPoint),
      priority: 1,
    };
    elevMarkers.push(elevMarker);
  }
  return elevMarkers;
}

export enum RoofEdgeDimType {
  VERTICAL = "VERTICAL",
  HORIZONTAL = "HORIZONTAL",
}

export interface RoofEdgeLine {
  dimType: RoofEdgeDimType;
  line: PolyLine2;
}

function roofEdgeLineNew(dimType: RoofEdgeDimType, line: PolyLine2): RoofEdgeLine {
  return {
    dimType,
    line,
  };
}

interface RoofEdgeAnnoInfo {
  roofSlopeTagInfos: RoofSlopeTagInfo[];
  roofEdgeLines: RoofEdgeLine[];
}

const ROOF_EDGE_OCCLUSION_PT_VERTICAL_OFFSET = 15;

interface AddRoofEdgeLineOptions {
  line: PolyLine3;
  line2: PolyLine2;
  roofEdgeLines: RoofEdgeLine[];
  rayOcclusionFaces: Face[];
  planeOffsetTowardsCamera: Plane;
  leastDistToViewPlane: number;
  viewPlane: Plane;
  dimType: RoofEdgeDimType;
}

function tryToAddRoofEdgeLine({
  line,
  line2,
  roofEdgeLines,
  rayOcclusionFaces,
  planeOffsetTowardsCamera,
  leastDistToViewPlane,
  viewPlane,
  dimType,
}: AddRoofEdgeLineOptions) {
  const [vnX, vnY] = planeNormal(viewPlane);

  // for roof edge lines, the full edge must be visible
  const [lsX, lsY, lsZ] = polyLine3StartPoint(line);
  const startOccluded = isPointOccludedByFaces(
    rayOcclusionFaces,
    lsX,
    lsY,
    lsZ + ROOF_EDGE_OCCLUSION_PT_VERTICAL_OFFSET,
    vnX,
    vnY,
    planeOffsetTowardsCamera,
    leastDistToViewPlane,
  );

  if (!startOccluded) {
    const [leX, leY, leZ] = polyLine3EndPoint(line);
    const endOccluded = isPointOccludedByFaces(
      rayOcclusionFaces,
      leX,
      leY,
      leZ + ROOF_EDGE_OCCLUSION_PT_VERTICAL_OFFSET,
      vnX,
      vnY,
      planeOffsetTowardsCamera,
      leastDistToViewPlane,
    );
    if (!endOccluded) {
      roofEdgeLines.push(roofEdgeLineNew(dimType, polyLine2Copy(line2)));
    }
  }
}

const FirstSampleDistDownRoofSlope = 18;
const ShiftDownRoofSlopeToClearOccludingRoof = 12;

export function extractUniqueRoofPlaneTags(
  viewPlane: Plane,
  levelRoofFaces: Table<RoofFace[]>,
  dormerLevelRoofFaces: Table<RoofFace[]>,
  roofAnnotationSettings: RoofAnnotationSettings = {},
): RoofEdgeAnnoInfo {
  // offset view plane towards camera for measuring roof plane z-order and assign offset dir
  const cameraOffsetDist = 10000;
  const viewPlaneForZDepthCalc = planeCopy(viewPlane);
  const planeOffsetTowardsCamera = planeMoveAlongNormal(viewPlaneForZDepthCalc, cameraOffsetDist);

  // create occlusion objects for visibility testing (todo: this could be shared by all elevations, if optimization is needed)
  const rayOcclusionFaces: Face[] = [];
  const unionedFacesByLevel: RWTable<RoofFaceInfo[]> = {};
  for (const levelId in levelRoofFaces) {
    const roofFaces = levelRoofFaces[levelId] ?? [];
    const unionedFaces = unionAdjacentCoplanarFaces(roofFaces);
    unionedFacesByLevel[levelId] = unionedFaces;
    for (const { outline, roofPlane } of unionedFaces) {
      const planeNorm = planeNormal(roofPlane);

      // don't extrude face if extrusion vector is in plane of face
      if (Math.abs(planeNorm[2]) < DIST_EPSILON) {
        continue;
      }

      // extrude roof faces downward to create occlusion masses
      const roofExtrusion = extrudeFace(faceNew([outline], roofPlane), 0, 0, -10000);
      const extrusion = extrusionToBRep(roofExtrusion);
      rayOcclusionFaces.push(...extrusion.faces);
    }
  }

  const unionedDormerFacesByLevel: RWTable<RoofFaceInfo[]> = {};
  for (const levelId in dormerLevelRoofFaces) {
    const roofFaces = dormerLevelRoofFaces[levelId] ?? [];
    const unionedFaces = unionAdjacentCoplanarFaces(roofFaces);
    unionedDormerFacesByLevel[levelId] = unionedFaces;
  }
  // retain unique roof planes for slope tag favoring the closest face to the view camera

  const roofEdgeLines: RoofEdgeLine[] = [];
  const uniqueRoofPlaneTags: RoofSlopeTagInfo[] = [];
  const [vnX, vnY] = planeNormal(viewPlane);

  for (const levelId in unionedFacesByLevel) {
    const unionedFaces = unionedFacesByLevel[levelId];
    for (const { type, roofPlane, outline, annotationSettings, isDormer } of unionedFaces) {
      const [pnX, pnY] = planeNormal(roofPlane);
      const dot = vec2Dot(pnX, pnY, vnX, vnY);

      // skip faces not pointing to the camera
      if (dot < -DIST_EPSILON) {
        continue;
      }

      const downwardSlopeVector = planeNormalizedDownwardSlopeVector(roofPlane);
      if (!downwardSlopeVector) {
        continue;
      }

      const [rX, rY, rZ] = downwardSlopeVector;

      // skip plane with purely vertical slope
      if (Math.abs(vec2Len(rX, rY)) < DIST_EPSILON) {
        continue;
      }

      // skip roof face with too small of area
      if (polyLine2Area(outline) < 1 /* sq in */) {
        continue;
      }

      const outerFaceLoop3 = polyLine3OnPlane(outline, roofPlane);
      let leastDistToViewPlane = Number.MAX_VALUE;
      let greatestDistToViewPlane = Number.MIN_VALUE;
      for (let i = 0, li = outerFaceLoop3.length - 3; i < li; i += 3) {
        const x = outerFaceLoop3[i];
        const y = outerFaceLoop3[i + 1];
        const z = outerFaceLoop3[i + 2];

        const distToCameraPlane = planeDistanceTo(planeOffsetTowardsCamera, x, y, z);
        if (distToCameraPlane < leastDistToViewPlane) {
          leastDistToViewPlane = distToCameraPlane;
        }
        if (distToCameraPlane > greatestDistToViewPlane) {
          greatestDistToViewPlane = distToCameraPlane;
        }
      }

      // create projection plane at roof face's nearest vertex to camera
      const faceVertexNearestCameraProjPlane = planeCopy(viewPlane);
      planeMoveAlongNormal(faceVertexNearestCameraProjPlane, leastDistToViewPlane);
      const faceProjectedOnNearestVertexPlane = faceProjectOuterLoopToPlane(
        faceNew([outline], roofPlane),
        faceVertexNearestCameraProjPlane,
      );

      const faceVertexFarthestFromCameraProjPlane = planeCopy(viewPlane);
      planeMoveAlongNormal(faceVertexFarthestFromCameraProjPlane, greatestDistToViewPlane);

      // abstract roof geometry to 2D line using bounding box (diagonal will match face geo in 2D)
      const { minX, minY, maxX, maxY } = polyLine2AABB2(faceProjectedOnNearestVertexPlane);

      const flipDir = vec3Dot(viewPlane.uX, viewPlane.uY, 0, rX, rY, 0) < 0;

      const roofTruncationHeightDimSetting = isDormer
        ? "showDormerRoofTruncationHeightDims"
        : "showRoofTruncationHeightDims";

      //if down slope vec is not orthogonal to view vec, try add to vertical dimensions and continue
      if (Math.abs(vec2Dot(vnX, vnY, rX, rY)) > DIST_EPSILON) {
        if (type === RoofFaceType.TRUNCATED) {
          const midX = (minX + maxX) * 0.5;
          const line2 = polyLine2New([maxX, minY, midX, maxY]);
          const line = polyLine3OnPlane(line2, faceVertexNearestCameraProjPlane);

          if (evalRoofAnnotationSetting(roofAnnotationSettings, annotationSettings, roofTruncationHeightDimSetting)) {
            tryToAddRoofEdgeLine({
              line,
              line2,
              roofEdgeLines,
              rayOcclusionFaces,
              planeOffsetTowardsCamera,
              leastDistToViewPlane,
              viewPlane,
              dimType: RoofEdgeDimType.VERTICAL,
            });
          }
        }

        continue;
      }

      const line2 = flipDir ? polyLine2New([minX, minY, maxX, maxY]) : polyLine2ByTwoPoints(maxX, minY, minX, maxY);
      const lineLen = polyLine2Length(line2);
      const line = polyLine3OnPlane(line2, faceVertexNearestCameraProjPlane);

      // sample point for roof ray
      let [cX, cY, cZ] = polyLine3EvalEdge(line, 0, lineLen - FirstSampleDistDownRoofSlope, false);

      const occluded = isPointOccludedByFaces(
        rayOcclusionFaces,
        cX,
        cY,
        cZ,
        vnX,
        vnY,
        planeOffsetTowardsCamera,
        leastDistToViewPlane,
      );

      if (occluded) {
        continue;
      }

      for (let i = FirstSampleDistDownRoofSlope; i < lineLen - 12; i += 3) {
        const [iX, iY, iZ] = polyLine3EvalEdge(line, 0, lineLen - i, false);

        const farOccluded = isPointOccludedByFaces(
          rayOcclusionFaces,
          iX,
          iY,
          iZ + 1,
          vnX,
          vnY,
          planeOffsetTowardsCamera,
          greatestDistToViewPlane,
        );

        if (!farOccluded) {
          if (i !== FirstSampleDistDownRoofSlope) {
            // shift down roof slope a bit so slope tag clears roof
            const [iX, iY, iZ] = polyLine3EvalEdge(
              line,
              0,
              lineLen - i - ShiftDownRoofSlopeToClearOccludingRoof,
              false,
            );
            cX = iX;
            cY = iY;
            cZ = iZ - 1;
          }

          break;
        }
      }

      // view orientation and roof plane flip
      const [annoX, annoY] = planeProject(viewPlane, cX, cY, cZ);

      const [sX, sY, sZ] = downwardSlopeVectorToElevSlopeFormat([rX, rY, rZ]);

      const flip = !flipDir;

      const roofSlopeElevationAnnotationSetting = isDormer
        ? "showDormerRoofSlopeElevationAnnotation"
        : "showRoofSlopeElevationAnnotation";

      if (evalRoofAnnotationSetting(roofAnnotationSettings, annotationSettings, roofSlopeElevationAnnotationSetting)) {
        uniqueRoofPlaneTags.push({
          roofPlane: planeCopy(roofPlane),
          distToViewPlane: leastDistToViewPlane,
          slope: [sX, sY, sZ],
          pos: [annoX, annoY],
          flip,
        });
      }

      const roofWidthDimsSetting = isDormer ? "showDormerRoofWidthDims" : "showRoofWidthDims";

      if (evalRoofAnnotationSetting(roofAnnotationSettings, annotationSettings, roofWidthDimsSetting)) {
        tryToAddRoofEdgeLine({
          line,
          line2,
          roofEdgeLines,
          rayOcclusionFaces,
          planeOffsetTowardsCamera,
          leastDistToViewPlane,
          viewPlane,
          dimType: RoofEdgeDimType.HORIZONTAL,
        });
      }
    }
  }

  for (const levelId in unionedDormerFacesByLevel) {
    const unionedFaces = unionedDormerFacesByLevel[levelId];
    if (!unionedFaces) {
      continue;
    }
    for (const { roofPlane, outline, annotationSettings, isDormer } of unionedFaces) {
      const downwardSlopeVector = planeNormalizedDownwardSlopeVector(roofPlane);
      if (!downwardSlopeVector) {
        continue;
      }

      const [rX, rY] = downwardSlopeVector;

      // skip plane with purely vertical slope
      if (Math.abs(vec2Len(rX, rY)) < DIST_EPSILON) {
        continue;
      }

      //if down slope vec is not orthogonal to view vec, continue
      if (Math.abs(vec2Dot(vnX, vnY, rX, rY)) > DIST_EPSILON) {
        continue;
      }

      const outerFaceLoop3 = polyLine3OnPlane(outline, roofPlane);
      let leastDistToViewPlane = Number.MAX_VALUE;
      for (let i = 0, li = outerFaceLoop3.length - 3; i < li; i += 3) {
        const x = outerFaceLoop3[i];
        const y = outerFaceLoop3[i + 1];
        const z = outerFaceLoop3[i + 2];

        const distToCameraPlane = planeDistanceTo(planeOffsetTowardsCamera, x, y, z);
        if (distToCameraPlane < leastDistToViewPlane) {
          leastDistToViewPlane = distToCameraPlane;
        }
      }

      // create projection plane at roof face's nearest vertex to camera
      const faceVertexNearestCameraProjPlane = planeCopy(viewPlane);
      planeMoveAlongNormal(faceVertexNearestCameraProjPlane, leastDistToViewPlane);
      const faceProjectedOnNearestVertexPlane = faceProjectOuterLoopToPlane(
        faceNew([outline], roofPlane),
        faceVertexNearestCameraProjPlane,
      );

      // abstract roof geometry to 2D line using bounding box (diagonal will match face geo in 2D)
      const { minX, minY, maxX, maxY } = polyLine2AABB2(faceProjectedOnNearestVertexPlane);
      const flipDir = vec3Dot(viewPlaneForZDepthCalc.uX, viewPlaneForZDepthCalc.uY, 0, rX, rY, 0) < 0;
      const line2 = flipDir ? polyLine2New([minX, minY, maxX, maxY]) : polyLine2ByTwoPoints(maxX, minY, minX, maxY);
      const line = polyLine3OnPlane(line2, faceVertexNearestCameraProjPlane);

      const flatRoofWidthDimsSetting = isDormer ? "showDormerRoofWidthDims" : "showRoofWidthDims";

      if (evalRoofAnnotationSetting(roofAnnotationSettings, annotationSettings, flatRoofWidthDimsSetting)) {
        tryToAddRoofEdgeLine({
          line,
          line2,
          roofEdgeLines,
          rayOcclusionFaces,
          planeOffsetTowardsCamera,
          leastDistToViewPlane,
          viewPlane,
          dimType: RoofEdgeDimType.HORIZONTAL,
        });
      }
    }
  }
  return {
    roofSlopeTagInfos: uniqueRoofPlaneTags,
    roofEdgeLines,
  };
}

function isPointOccludedByFaces(
  rayOcclusionFaces: Face[],
  cX: number,
  cY: number,
  cZ: number,
  vnX: number,
  vnY: number,
  planeOffsetTowardsCamera: Plane,
  leastDistToViewPlane: number,
) {
  let occluded = false;
  for (const face of rayOcclusionFaces) {
    const res = intersectFaceRay(face, cX, cY, cZ, vnX, vnY, 0);
    if (res) {
      const [u, v] = res;
      const [x, y, z] = planeEval(face.plane, u, v);
      if (planeDistanceTo(planeOffsetTowardsCamera, x, y, z) < leastDistToViewPlane) {
        occluded = true;
        break;
      }
    }
  }
  return occluded;
}

const ROOF_EDGE_GROUP_ANGLE_DEGREE_TOLERANCE = 2;
const ROOF_EGE_GROUP_PERP_DISTANCE_TOLERANCE = 2;
const ROOF_EDGE_GROUP_OVERLAP_EXTENSION = 5;

export function groupRoofEdgeLines(roofEdgeLines: RoofEdgeLine[]): LineWithParamRange[][] {
  const groupedParallelLinesArray: LineWithParamRange[][] = [];
  const markedIndices: { [index: number]: boolean } = {};
  for (const lineIndex of Object.keys(roofEdgeLines)) {
    if (markedIndices[lineIndex]) {
      continue;
    }

    const { dimType, line } = roofEdgeLines[lineIndex];

    const [sX, sY] = polyLine2Start(line);
    const [eX, eY] = polyLine2End(line);
    const [vX, vY] = line2Vec([sX, sY, eX, eY]);

    const groupedLines: LineWithParamRange[] = [];
    const pl = polyLine2ByTwoPoints(sX, sY, eX, eY);

    groupedLines.push({
      startParam: 0,
      endParam: polyLine2Length(pl),
      line: pl,
      dimType,
    });
    markedIndices[lineIndex] = true;

    for (const otherLineIndex of Object.keys(roofEdgeLines)) {
      if (otherLineIndex === lineIndex) {
        continue;
      }
      if (markedIndices[otherLineIndex]) {
        continue;
      }

      const { dimType: otherDimType, line: otherLine } = roofEdgeLines[otherLineIndex];

      if (dimType !== otherDimType) {
        continue;
      }

      const [osX, osY] = polyLine2Start(otherLine);
      const [oeX, oeY] = polyLine2End(otherLine);
      const [ovX, ovY] = line2Vec([osX, osY, oeX, oeY]);

      const angle = Math.abs(vec2AngleBetween(vX, vY, ovX, ovY));
      const thresholdForParallel = degreeToRadian(ROOF_EDGE_GROUP_ANGLE_DEGREE_TOLERANCE);

      const startPerpDistance = ray2PerpDistance(sX, sY, vX, vY, osX, osY) < ROOF_EGE_GROUP_PERP_DISTANCE_TOLERANCE;
      const endPerpDistance = ray2PerpDistance(sX, sY, vX, vY, oeX, oeY) < ROOF_EGE_GROUP_PERP_DISTANCE_TOLERANCE;

      if (angle < thresholdForParallel && (startPerpDistance || endPerpDistance)) {
        const startParam = ray2DotProject(sX, sY, vX, vY, osX, osY);
        const endParam = ray2DotProject(sX, sY, vX, vY, oeX, oeY);
        const minParam = Math.min(startParam, endParam);
        const startIsMin = Math.abs(minParam - startParam) < DIST_EPSILON;

        groupedLines.push({
          startParam: minParam,
          endParam: startIsMin ? endParam : startParam,
          line: startIsMin ? polyLine2ByTwoPoints(osX, osY, oeX, oeY) : polyLine2ByTwoPoints(oeX, oeY, osX, osY),
          dimType,
        });

        markedIndices[otherLineIndex] = true;
      }
    }

    groupedParallelLinesArray.push(groupedLines);
  }
  return groupedParallelLinesArray;
}

interface LineWithParamRange {
  startParam: number;
  endParam: number;
  line: PolyLine2;
  dimType: RoofEdgeDimType;
}

export function joinRoofEdgesWithOverlappingIntervals(
  groupedParallelLinesArray: LineWithParamRange[][],
): RoofEdgeLine[] {
  const joinedLines: RoofEdgeLine[] = [];
  for (const groupedLines of groupedParallelLinesArray) {
    groupedLines.sort((x) => x.startParam);

    let startPt: Vec2 | undefined;
    let endPt: Vec2 | undefined;
    let startParam: number | undefined;
    let endParam: number | undefined;

    for (let i = 0; i < groupedLines.length; i++) {
      const {
        line: [csX, csY, ceX, ceY],
        startParam: cStartParam,
        endParam: cEndParam,
        dimType,
      } = groupedLines[i];

      if (i === 0) {
        if (groupedLines.length === 1) {
          joinedLines.push(roofEdgeLineNew(dimType, polyLine2ByTwoPoints(csX, csY, ceX, ceY)));
          continue;
        }

        startPt = [csX, csY];
        endPt = [ceX, ceY];
        startParam = cStartParam;
        endParam = cEndParam;

        continue;
      }

      assertIsNotUndefined(startPt);
      assertIsNotUndefined(endPt);

      const [sX, sY] = startPt;
      const [eX, eY] = endPt;

      assertIsNotUndefined(startParam);
      assertIsNotUndefined(endParam);

      const overlap = intervalsOverlap(
        startParam - ROOF_EDGE_GROUP_OVERLAP_EXTENSION,
        endParam + ROOF_EDGE_GROUP_OVERLAP_EXTENSION,
        cStartParam - ROOF_EDGE_GROUP_OVERLAP_EXTENSION,
        cEndParam + ROOF_EDGE_GROUP_OVERLAP_EXTENSION,
      );

      if (!overlap) {
        joinedLines.push(roofEdgeLineNew(dimType, polyLine2ByTwoPoints(sX, sY, eX, eY)));
        startPt = [csX, csY];
        endPt = [ceX, ceY];
        startParam = cStartParam;
        endParam = cEndParam;
        if (i === groupedLines.length - 1) {
          joinedLines.push(roofEdgeLineNew(dimType, polyLine2ByTwoPoints(csX, csY, ceX, ceY)));
        }
        continue;
      }

      endPt = [ceX, ceY];

      if (cEndParam > endParam) {
        endParam = cEndParam;
      }

      if (i === groupedLines.length - 1) {
        joinedLines.push(roofEdgeLineNew(dimType, polyLine2ByTwoPoints(sX, sY, ceX, ceY)));
      }
    }
  }

  return joinedLines;
}
